import Template from "ui/Template";
import DragController from '../../helpers/DragController';
import toolboxView from './toolbox.handlebars';

/**
 * Tool Box Element
 */
class ToolBox {
	/**
	 * Class constructor
	 *
	 * @param {Object} [options]
	 */
	constructor(options = {}) {
		/**
		 * Instance of the HTML Element
		 *
		 * @type {Element}
		 */
		this.element = new Template(toolboxView)
			.createElement({
				title: options.title || 'Untitled',
				isCollapsable: options.isCollapsable === true,
				isPinnedAtRight: options.isPinnedAtRight === true,
				isPinnedAtLeft: options.isPinnedAtLeft === true,
				isPinnedAtBottom: options.isPinnedAtBottom === true,
				isResizeTop: options.isResizeTop === true
			});

		/**
		 * Instance of the editor
		 *
		 * @type {SceneEditor}
		 */
		this.scene = options.scene;

		/**
		 * Body element
		 *
		 * @type {Element}
		 */
		this.body = null;

		/**
		 * @type {number}
		 * @private
		 */
		this._height = typeof options.height === 'number' ? options.height : 0;

		this._els = {
			title: this.element.querySelector('.editor-toolbox-title'),
			titleCaption: this.element.querySelector('.editor-toolbox-title__caption'),
			titleBtnCollapse: this.element.querySelector('.editor-toolbox-title__button_collapse'),
			bodyCont: this.element.querySelector('.editor-toolbox__body'),
			resizeTop: null
		};

		if (options.isResizeTop) {
			this._els.resizeTop = this.element.querySelector('.editor-toolbox__resize-top');
		}

		if (this._height > 0) {
			this.element.style.height = this._height + 'px';
		}

		if (options.isDraggable === true) {
			this._enableDragging();
		}

		this._attachEventListeners();

		if (options.parent instanceof HTMLElement) {
			options.parent.appendChild(this.element);
		}
	}

	toggle(flag = true) {
		this.element.hidden = !flag;
		this.scene.rearrangeToolbars();
	}

	/**
	 * Sets body element
	 *
	 * @param {Element} el
	 * @returns {ToolBox}
	 */
	setBodyElement(el) {
		this.body = el;
		this._els.bodyCont.innerHTML = '';
		this._els.bodyCont.appendChild(el);
		return this;
	}

	/**
	 * Collapses/expands toolbox body
	 *
	 * @param {boolean} flag
	 * @returns {ToolBox}
	 */
	collapseBody(flag) {
		this.element.classList.toggle('editor-toolbox_collapsed', flag === true);
		this.scene.rearrangeToolbars();
		return this;
	}

	/**
	 * Returns TRUE if body element collapsed
	 *
	 * @returns {boolean}
	 */
	isBodyCollapsed() {
		return this.element.classList.contains('editor-toolbox_collapsed');
	}

	/**
	 * Returns TRUE if the toolbox has been pinned
	 *
	 * @returns {boolean}
	 */
	isPinned() {
		let classList = this.element.classList;
		return classList.contains('editor-toolbox_pin-right')
			|| classList.contains('editor-toolbox_pin-left');
	}

	/**
	 * Attaches event listeners
	 *
	 * @private
	 * @returns {void}
	 */
	_attachEventListeners() {
		this._els.titleBtnCollapse.addEventListener('click', () => {
			this.collapseBody(!this.isBodyCollapsed());
		});
		this._attachResizeTopEvents();
	}

	/**
	 * Attaches events listeners to Resize Top border
	 *
	 * @private
	 * @returns {void}
	 */
	_attachResizeTopEvents() {
		if (!this._els.resizeTop) {
			return;
		}
		let isDragging = false, newHeight,
			screenSize = this.scene.screenSize;
		new DragController({
			element: this._els.resizeTop,
			start: () => {
				isDragging = true;
				newHeight = this._height;
			},
			move: (dx, dy) => {
				if (!isDragging) {
					return;
				}
				newHeight = Math.min(~~(screenSize.height / 2), 1024, Math.max(100, this._height - dy));
				this.element.style.height = String(newHeight) + 'px';
			},
			finish: () => {
				if (!isDragging) {
					return;
				}
				this._height = newHeight;
			}
		});
	}

	/**
	 * Enables Drag & Drop mode for toolbox
	 *
	 * @private
	 * @returns {void}
	 */
	_enableDragging() {
		let rect, el = this.element, isDragging = false,
			isDragEnter = false, dragZone = '',
			parentRect, pinIndent = 150, timer = 0,
			newPos = {x: 0, y: 0};

		const checkDragZone = () => {
			if (newPos.x + rect.width > parentRect.width - pinIndent) {
				if (!isDragEnter) {
					isDragEnter = true;
					this.scene.toggleRightToolbarDragZone(true);
					el.classList.add('editor-toolbox_drag-enter');
					dragZone = 'right';
				}
			} else if (newPos.x < pinIndent) {
				if (!isDragEnter) {
					isDragEnter = true;
					this.scene.toggleLeftToolbarDragZone(true);
					el.classList.add('editor-toolbox_drag-enter');
					dragZone = 'left';
				}
			} else if (isDragEnter) {
				isDragEnter = false;
				if (dragZone === 'left') {
					this.scene.toggleLeftToolbarDragZone(false);
				} else {
					this.scene.toggleRightToolbarDragZone(false);
				}
				el.classList.remove('editor-toolbox_drag-enter');
			}
		};

		new DragController({
			element: this._els.titleCaption,
			start: () => {
				isDragging = false;
				if (timer > 0) {
					clearTimeout(timer);
				}
				timer = setTimeout(() => {
					isDragging = true;
					rect = el.getBoundingClientRect();
					parentRect = el.parentNode.getBoundingClientRect();
					el.classList.remove('editor-toolbox_pin-right');
					el.classList.remove('editor-toolbox_pin-left');
					newPos.x = rect.left - parentRect.left;
					newPos.y = rect.top - parentRect.top;
					el.style.left = String(Math.round(newPos.x)) + 'px';
					el.style.top = String(Math.round(newPos.y)) + 'px';
					checkDragZone();
				}, !this.isPinned() ? 0 : 200);
			},
			move: (dx, dy) => {
				if (!isDragging) {
					return;
				}
				newPos.x = rect.left - parentRect.left + dx;
				newPos.y = rect.top - parentRect.top + dy;
				el.style.left = String(Math.round(newPos.x)) + 'px';
				el.style.top = String(Math.round(newPos.y)) + 'px';
				checkDragZone();
			},
			finish: () => {
				if (timer > 0) {
					clearTimeout(timer);
				}
				if (!isDragging) {
					return;
				}
				if (isDragEnter) {
					isDragEnter = false;
					el.style.left = '';
					el.classList.remove('editor-toolbox_drag-enter');
					if (dragZone === 'left') {
						el.classList.add('editor-toolbox_pin-left');
					} else {
						el.classList.add('editor-toolbox_pin-right');
					}
				}
				this.scene.toggleRightToolbarDragZone(false)
					.toggleLeftToolbarDragZone(false)
					.rearrangeToolbars();
			}
		});
	}
}

export default ToolBox;
